import { PATHS } from '../paths';

/**
 * A row the client can open, named the way the client thinks about it.
 *
 * Deliberately **not** keyed on the assistant's `EntityRefType`: that union is
 * a model-facing choice set (`@orn/global/assistant/inlineMarkup.js`) whose
 * five members happen to coincide with this one today, while this one names
 * every record kind a surface can open. Keying on it would force a surface
 * that has nothing to do with the assistant to either widen the model's
 * grammar or grow a second copy of this mapping.
 */
export type EntityTarget =
  | { type: 'merchant'; id: string }
  | { type: 'financialAccount'; id: string }
  | { type: 'transaction'; id: string }
  | { type: 'bill'; id: string }
  | { type: 'recurringTransaction'; id: string };

/**
 * Where opening a row goes. Every kind has a route, so a destination is the
 * path to navigate to; the `kind` discriminant stays so a consumer switching
 * on it keeps compiling if a kind ever needs a different surface again.
 */
export type EntityDestination = { kind: 'navigate'; path: string };

/**
 * Resolve a row to the thing that opens it.
 *
 * Total by construction — every target has somewhere to go, so a caller never
 * has to decide what an unopenable row looks like. A new member of
 * {@link EntityTarget} does not compile until it picks a destination.
 */
export function entityDestination(target: EntityTarget): EntityDestination {
  switch (target.type) {
    case 'merchant':
      return { kind: 'navigate', path: PATHS.MERCHANT(target.id) };
    case 'financialAccount':
      return { kind: 'navigate', path: PATHS.ACCOUNT(target.id) };
    case 'transaction':
      return { kind: 'navigate', path: PATHS.TRANSACTION(target.id) };
    case 'bill':
      return { kind: 'navigate', path: PATHS.BILL(target.id) };
    case 'recurringTransaction':
      return { kind: 'navigate', path: PATHS.RECURRING_TRANSACTION(target.id) };
    default: {
      const _exhaustive: never = target;
      return _exhaustive;
    }
  }
}

/**
 * The path a row is linked to. A chip renders an anchor to it, so cmd-click
 * and copy-link-address behave the way they do everywhere else.
 */
export function entityHref(target: EntityTarget): string {
  return entityDestination(target).path;
}
